import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TenantResolutionService } from './tenant-resolution.service';

const AUTH_POPUP_WINDOW_NAME = 'cb_auth_popup';
const AUTH_POPUP_WIDTH = 500;
const AUTH_POPUP_HEIGHT = 650;
const AUTH_POPUP_CALLBACK_PATH = '/auth-popup-callback.html';
const AUTH_POPUP_DONE_MESSAGE = 'auth-popup-done';
const AUTH_POPUP_ERROR_MESSAGE = 'auth-popup-error';
const POPUP_POLL_INTERVAL_MS = 500;

/**
 * User profile returned by the BFF auth profile endpoint.
 */
export interface BffUserProfile {
  sub: string;
  email: string;
  name?: string;
  roles: string[];
  tenantId?: string | null;
}

/**
 * BFF-based auth service for the public frontend.
 * The oauth2-proxy handles session cookies & token injection.
 * This service only fetches the user profile and provides login/logout helpers.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private _profile$ = new BehaviorSubject<BffUserProfile | null>(null);
  private _loaded$ = new BehaviorSubject<boolean>(false);

  /** BroadcastChannel for cross-tab logout propagation. */
  private readonly authChannel = new BroadcastChannel('auth');

  /** Tenant-URL-derived `kc_idp_hint` fallback; per-assistant value still wins. */
  private readonly tenantResolution = inject(TenantResolutionService);

  readonly profile$ = this._profile$.asObservable();
  readonly loaded$ = this._loaded$.asObservable();

  constructor(private http: HttpClient) {
    this.authChannel.onmessage = (event) => {
      if (event.data?.type === 'logout') {
        // Another tab logged out — clear local state.
        // The public frontend allows anonymous access, so just clearing
        // the profile is enough (no redirect needed).
        this._profile$.next(null);
        this._loaded$.next(true);
      }
    };
  }

  ngOnDestroy(): void {
    this.authChannel.close();
  }

  get profile(): BffUserProfile | null {
    return this._profile$.value;
  }

  get isAuthenticated(): boolean {
    return !!this._profile$.value;
  }

  get loaded(): boolean {
    return this._loaded$.value;
  }

  get username(): string {
    const p = this._profile$.value;
    return p?.name || p?.email || '';
  }

  /**
   * Fetches the user profile from the server.
   * Called during APP_INITIALIZER. If the user has no session cookie
   * (i.e. not logged in), returns null silently (public access allowed).
   */
  fetchProfile(): Promise<BffUserProfile | null> {
    return firstValueFrom(
      this.http.get<BffUserProfile>('/api/v1/auth/profile').pipe(
        tap((profile) => {
          this._profile$.next(profile);
        }),
        catchError(() => {
          this._profile$.next(null);
          return of(null);
        }),
        tap(() => this._loaded$.next(true)),
      ),
    );
  }

  /**
   * Extracts the dataset query param from a URL (if present)
   * so it can be forwarded as a top-level param to Keycloak.
   */
  private getDatasetParam(uri?: string): string {
    try {
      const url = new URL(uri || window.location.href);
      const dataset = url.searchParams.get('dataset');
      return dataset ? 'dataset=' + encodeURIComponent(dataset) + '&' : '';
    } catch {
      return '';
    }
  }

  /**
   * Builds the `rd` (redirect-after-login) value.
   *
   * When an explicit `redirectUri` is provided (full URL), we always pass it
   * as-is so oauth2-proxy sends the user back to exactly that URL — including
   * on custom tenant domains like https://www.umbrella.corp/.
   * All valid target domains (*.omm.cloud + tenant hosts) are listed in
   * OAUTH2_PROXY_WHITELIST_DOMAINS via helm, so full URLs are accepted.
   *
   * When no redirectUri is given, fall back to a relative path (safe default).
   */
  private buildRedirectParam(redirectUri?: string): string {
    if (!redirectUri) {
      return window.location.pathname + window.location.search;
    }
    // Always use the full URL when explicitly provided — this is the only way
    // to get oauth2-proxy to redirect back to a custom domain after login.
    return redirectUri;
  }

  /**
   * Redirect to oauth2-proxy login.
   *
   * When called from a custom tenant domain (e.g. https://www.umbrella.corp/),
   * we must preserve the full URL as `rd` so oauth2-proxy redirects back to that
   * domain after authentication — not to the main app origin (p2.cb.omm.cloud).
   * The tenant domains are added to OAUTH2_PROXY_WHITELIST_DOMAINS via helm values,
   * so oauth2-proxy accepts the cross-domain `rd` parameter.
   *
   * When no explicit redirectUri is passed (fallback), use the relative path so it
   * always works on the main domain even without explicit whitelisting.
   */
  /** Caller-supplied hint wins over the tenant-URL fallback; empty when neither is set. */
  private buildIdpHintParam(kcIdpHint?: string): string {
    const effectiveHint = kcIdpHint ?? this.tenantResolution.idpHint ?? undefined;
    return effectiveHint ? 'kc_idp_hint=' + encodeURIComponent(effectiveHint) + '&' : '';
  }

  login(redirectUri?: string, allowRegistration = false, kcIdpHint?: string): void {
    const rd = this.buildRedirectParam(redirectUri);
    const registrationParam = allowRegistration ? 'registration=allow&' : '';
    const datasetParam = this.getDatasetParam(redirectUri);
    const idpHintParam = this.buildIdpHintParam(kcIdpHint);
    this.navigateTo('/oauth2/start?' + registrationParam + datasetParam + idpHintParam + 'rd=' + encodeURIComponent(rd));
  }

  /**
   * Open a popup window for the oauth2-proxy login flow.
   * Used when the chat is embedded in a cross-origin iframe where redirect-based
   * login is blocked by third-party cookie / frame-ancestors restrictions.
   *
   * The popup navigates through the normal oauth2-proxy → Keycloak flow and
   * lands on `/auth-popup-callback.html` which posts a message back. Once
   * received, this method re-fetches the user profile and resolves the promise.
   *
   * @returns true if the user successfully authenticated via the popup.
   */
  // TODO: The popup auth logic (loginPopup, registerPopup, openAuthPopup)
  // is duplicated in management-app/frontend/src/app/services/auth/auth.service.ts.
  // Extract into a shared library in common/ when feasible.

  loginPopup(allowRegistration = false, kcIdpHint?: string): Promise<boolean> {
    const registrationParam = allowRegistration ? 'registration=allow&' : '';
    const idpHintParam = this.buildIdpHintParam(kcIdpHint);
    const popupUrl = '/oauth2/start?' + registrationParam + idpHintParam + 'rd=' + encodeURIComponent(AUTH_POPUP_CALLBACK_PATH);
    return this.openAuthPopup(popupUrl);
  }

  /**
   * Redirect directly to Keycloak registration form.
   * Uses /oauth2/register — an nginx/proxy endpoint that proxies to
   * oauth2-proxy's /oauth2/start but rewrites the Keycloak redirect from
   * /openid-connect/auth → /openid-connect/registrations so the browser
   * lands on the registration page instead of the login page.
   * State/nonce/PKCE are handled normally by oauth2-proxy.
   */
  register(redirectUri?: string): void {
    const rd = redirectUri
      ? new URL(redirectUri).pathname + new URL(redirectUri).search
      : window.location.pathname + window.location.search;
    const datasetParam = this.getDatasetParam(redirectUri);
    this.navigateTo('/oauth2/register?registration=allow&' + datasetParam + 'rd=' + encodeURIComponent(rd));
  }

  /**
   * Open a popup window for the registration flow (same as loginPopup but
   * targets the /oauth2/register endpoint).
   *
   * @returns true if the user successfully authenticated via the popup.
   */
  registerPopup(kcIdpHint?: string): Promise<boolean> {
    const idpHintParam = this.buildIdpHintParam(kcIdpHint);
    const popupUrl = '/oauth2/register?registration=allow&' + idpHintParam + 'rd=' + encodeURIComponent(AUTH_POPUP_CALLBACK_PATH);
    return this.openAuthPopup(popupUrl);
  }

  /**
   * Opens a centered popup window to the given URL and waits for the auth
   * callback page to post a success/error message back via postMessage.
   * Falls back to polling the popup's closed state if postMessage doesn't arrive.
   *
   * @returns true if the user successfully authenticated via the popup.
   */
  private openAuthPopup(popupUrl: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const left = window.screenX + (window.outerWidth - AUTH_POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - AUTH_POPUP_HEIGHT) / 2;
      const popup = window.open(
        popupUrl,
        AUTH_POPUP_WINDOW_NAME,
        `width=${AUTH_POPUP_WIDTH},height=${AUTH_POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
      );

      if (!popup) {
        console.error('Auth popup was blocked by the browser. Please allow popups for this site.');
        resolve(false);
        return;
      }

      let settled = false;
      let pollTimer: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        if (pollTimer) clearInterval(pollTimer);
      };

      const onMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === AUTH_POPUP_DONE_MESSAGE) {
          cleanup();
          // The popup creates a one-time exchange token backed by its _sid
          // cookie. The iframe redeems it to receive the _sid value as a
          // Partitioned cookie — reusing oauth2-proxy's session validation.
          if (event.data.exchangeToken) {
            try {
              await this.establishIframeSession(event.data.exchangeToken);
            } catch {
              // Session cookie could not be set — the user would appear logged in
              // but all API calls would fail. Clear the profile to avoid the
              // inconsistent state and let the user retry login.
              this._profile$.next(null);
              this._loaded$.next(true);
              resolve(false);
              return;
            }
          }
          if (event.data.profile) {
            this._profile$.next(event.data.profile);
            this._loaded$.next(true);
          } else {
            await this.fetchProfile();
          }
          resolve(this.isAuthenticated);
        } else if (event.data?.type === AUTH_POPUP_ERROR_MESSAGE) {
          cleanup();
          resolve(false);
        }
      };

      window.addEventListener('message', onMessage);

      // Poll to detect if the popup was closed without completing login.
      // If the popup closed without postMessage, try fetchProfile
      // as a last resort (works when not in a cross-origin iframe).
      pollTimer = setInterval(() => {
        if (popup.closed) {
          cleanup();
          this.fetchProfile()
            .then(() => resolve(this.isAuthenticated));
        }
      }, POPUP_POLL_INTERVAL_MS);
    });
  }

  /**
   * Redeems a one-time exchange token from the popup to receive the
   * oauth2-proxy `_sid` session cookie as a `Partitioned` cookie.
   * After this call, all subsequent requests from the iframe automatically
   * include the `_sid` cookie — reusing oauth2-proxy's existing session
   * validation (nginx auth_request → token injection).
   */
  private async establishIframeSession(exchangeToken: string): Promise<void> {
    const response = await fetch('/api/v1/auth/iframe-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exchangeToken }),
      credentials: 'include',
    });

    if (!response.ok) {
      console.error('Failed to establish iframe session', response.status, response.statusText);
      throw new Error('Failed to establish iframe session');
    }
  }

  /**
   * Sends a lightweight keepalive ping to prevent the session cookie
   * from expiring.
   *
   * Hits `GET /oauth2/userinfo` directly — this goes to oauth2-proxy
   * (not through nginx auth_request) so the `COOKIE_REFRESH` mechanism
   * can issue a fresh `Set-Cookie` header back to the browser.
   *
   * A backend profile call (`/api/v1/auth/profile`) is NOT needed here:
   * it flows through nginx `auth_request` which does NOT forward
   * `Set-Cookie` back to the browser, so it cannot refresh the cookie.
   * If the token becomes invalid, the next user-initiated API call will
   * trigger the 401 recovery flow.
   */
  sendSessionPing(): void {
    if (!this.isAuthenticated) return;
    fetch('/oauth2/userinfo', { credentials: 'include' }).catch(() => {});
  }

  /**
   * Redirect to oauth2-proxy logout, then to Keycloak SSO logout.
   * The backchannel call is best-effort; the local session is always cleared.
   */
  logout(): void {
    // 1. Attempt to revoke the Keycloak SSO session server-side (best effort).
    // 2. ALWAYS clear the local oauth2-proxy session cookie.
    fetch('/api/v1/auth/logout', { method: 'POST' })
      .catch((err) => {
        console.error('Backchannel logout failed, proceeding with local logout.', err);
      })
      .finally(() => {
        this.authChannel.postMessage({ type: 'logout' });
        this.navigateTo('/oauth2/sign_out?rd=' + encodeURIComponent(window.location.pathname + window.location.search));
      });
  }

  navigateTo(url: string): void {
    window.location.href = url;
  }
}
